import React, { useReducer, useCallback, useEffect, useState, lazy, Suspense } from 'react';
import { gameReducer, createInitialState, ACTION_TYPES, SCREENS } from './engine/gameState.js';
import { storyNodes } from './data/story.js';
import { useCodex } from './hooks/useCodex.js';
import { useUser } from './hooks/useUser.js';

import TitleScreen from './components/screens/TitleScreen.jsx';
import AuthModal from './components/auth/AuthModal.jsx';

// Secondary screens are split so the initial title-screen bundle stays small.
// CodexScreen pulls in @xyflow/react + dagre — kept in the lazy chunk.
const CharacterSelect = lazy(() => import('./components/screens/CharacterSelect.jsx'));
const StoryScreen = lazy(() => import('./components/screens/StoryScreen.jsx'));
const BattleScreen = lazy(() => import('./components/screens/BattleScreen.jsx'));
const CodexScreen = lazy(() => import('./components/screens/CodexScreen.jsx'));

const ScreenFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <p className="font-mono text-ink/50 text-sm tracking-wider">CHARGEMENT…</p>
  </div>
);

export default function App() {
  const [state, dispatch] = useReducer(gameReducer, null, createInitialState);
  const { user, isAnonymous, isAuthenticated, signIn, signUp, signInWithGoogle, signOut } = useUser();
  const { codex, syncStatus, recordVisit, recordEnding, recordCharacter } = useCodex(user);
  const [authModal, setAuthModal] = useState({ open: false, mode: 'signin' });

  const openAuth = useCallback((mode = 'signin') => {
    setAuthModal({ open: true, mode });
  }, []);

  const closeAuth = useCallback(() => {
    setAuthModal((prev) => ({ ...prev, open: false }));
  }, []);

  // Persist visited nodes & endings to the codex whenever the story progresses
  useEffect(() => {
    if (state.storyNodeId) {
      recordVisit(state.storyNodeId);
      const node = storyNodes[state.storyNodeId];
      if (node?.type === 'ending' && node.ending?.id) {
        recordEnding(node.ending.id);
      }
    }
  }, [state.storyNodeId, recordVisit, recordEnding]);

  // Track character played
  useEffect(() => {
    if (state.player?.characterId) {
      recordCharacter(state.player.characterId);
    }
  }, [state.player?.characterId, recordCharacter]);

  const handleNewGame = useCallback(() => {
    dispatch({ type: ACTION_TYPES.START_GAME });
  }, []);

  const handleSelectCharacter = useCallback((characterId) => {
    dispatch({ type: ACTION_TYPES.SELECT_CHARACTER, characterId });
  }, []);

  const handleStoryAdvance = useCallback((nodeId) => {
    dispatch({ type: ACTION_TYPES.STORY_GO_TO, nodeId });
  }, []);

  const handleStoryChoice = useCallback((choiceIndex) => {
    dispatch({ type: ACTION_TYPES.STORY_CHOICE, choiceIndex });
  }, []);

  const handleEnterCombat = useCallback(() => {
    dispatch({ type: ACTION_TYPES.STORY_TO_COMBAT });
  }, []);

  const handlePlayerMove = useCallback((move) => {
    dispatch({ type: ACTION_TYPES.PLAYER_MOVE, move });
  }, []);

  const handlePlayerUseItem = useCallback((itemId) => {
    dispatch({ type: ACTION_TYPES.PLAYER_USE_ITEM, itemId });
  }, []);

  const handleBossTurn = useCallback(() => {
    dispatch({ type: ACTION_TYPES.BOSS_TURN });
  }, []);

  const handleBackToTitle = useCallback(() => {
    dispatch({ type: ACTION_TYPES.BACK_TO_TITLE });
  }, []);

  const handleOpenCodex = useCallback(() => {
    dispatch({ type: ACTION_TYPES.OPEN_CODEX });
  }, []);

  const handleCloseCodex = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLOSE_CODEX });
  }, []);

  const authModalEl = (
    <AuthModal
      open={authModal.open}
      defaultMode={authModal.mode}
      isAnonymous={isAnonymous}
      onClose={closeAuth}
      onSignIn={signIn}
      onSignUp={signUp}
      onSignInWithGoogle={signInWithGoogle}
    />
  );

  const renderScreen = () => {
    switch (state.screen) {
      case SCREENS.TITLE:
        return (
          <>
            <TitleScreen
              onNewGame={handleNewGame}
              onOpenCodex={handleOpenCodex}
              user={user}
              isAuthenticated={isAuthenticated}
              onOpenAuth={openAuth}
              onSignOut={signOut}
            />
            {authModalEl}
          </>
        );

      case SCREENS.CHARACTER_SELECT:
        return <CharacterSelect onSelect={handleSelectCharacter} />;

      case SCREENS.STORY:
        return (
          <StoryScreen
            state={state}
            onChoice={handleStoryChoice}
            onAdvance={handleStoryAdvance}
            onEnterCombat={handleEnterCombat}
            onBackToTitle={handleBackToTitle}
            onOpenCodex={handleOpenCodex}
          />
        );

      case SCREENS.BATTLE:
        return (
          <BattleScreen
            state={state}
            onPlayerMove={handlePlayerMove}
            onPlayerUseItem={handlePlayerUseItem}
            onBossTurn={handleBossTurn}
          />
        );

      case SCREENS.CODEX:
        return (
          <>
            <CodexScreen
              codex={codex}
              syncStatus={syncStatus}
              onClose={handleCloseCodex}
              currentNodeId={state.storyNodeId}
              user={user}
              isAuthenticated={isAuthenticated}
              onOpenAuth={openAuth}
              onSignOut={signOut}
            />
            {authModalEl}
          </>
        );

      default:
        return (
          <div className="min-h-screen flex items-center justify-center">
            <p className="font-sans text-stamp-red">Erreur 404 : Écran introuvable. Comme ton dossier.</p>
          </div>
        );
    }
  };

  return (
    <main>
      <Suspense fallback={<ScreenFallback />}>{renderScreen()}</Suspense>
    </main>
  );
}
